import { useEffect } from 'react';

export default function Landing() {
  useEffect(() => {
    document.title = 'verticalstack — Multi-Tenant SaaS Property Management';
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        :root {
          --vs-cyan-blue:       #00C2D4;
          --vs-dark-navy:       #1A3266;
          --vs-vibrant-cyan:    #00E5FF;
          --vs-slate:           #4D6070;
          --vs-deep-dark:       #0D1B2A;
          --vs-light-blue-gray: #E5EEF2;
          --vs-off-white:       #F4F8FB;
          --vs-tag-gray:        #8A9BAA;
          --vs-white:           #FFFFFF;
          --vs-font-primary: 'Inter', 'Nunito Sans', system-ui, -apple-system, sans-serif;
        }

        .vs-root * { box-sizing: border-box; margin: 0; padding: 0; }
        .vs-root {
          font-family: var(--vs-font-primary);
          font-weight: 400;
          color: var(--vs-deep-dark);
          background: var(--vs-off-white);
          line-height: 1.6;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        .vs-root a { color: var(--vs-cyan-blue); text-decoration: none; transition: color 0.15s ease; }
        .vs-root a:hover { color: var(--vs-dark-navy); }
        .vs-container { max-width: 1140px; margin: 0 auto; padding: 0 24px; }

        /* Nav */
        .vs-nav {
          position: sticky; top: 0; z-index: 100;
          background: var(--vs-off-white);
          border-bottom: 1px solid var(--vs-light-blue-gray);
          backdrop-filter: saturate(180%) blur(8px);
          -webkit-backdrop-filter: saturate(180%) blur(8px);
        }
        .vs-nav-inner {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 24px; max-width: 1140px; margin: 0 auto;
        }
        .vs-nav-logo img { height: 32px; width: auto; display: block; }
        .vs-nav-links { display: flex; gap: 28px; align-items: center; }
        .vs-nav-links a { color: var(--vs-slate); font-weight: 500; font-size: 14px; }
        .vs-nav-links a:hover { color: var(--vs-dark-navy); }
        .vs-nav-login {
          color: var(--vs-dark-navy) !important;
          font-weight: 600 !important;
          border: 1.5px solid var(--vs-dark-navy);
          padding: 8px 18px;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .vs-nav-login:hover {
          background: var(--vs-dark-navy);
          color: var(--vs-white) !important;
        }
        .vs-nav-cta {
          background: var(--vs-cyan-blue);
          color: var(--vs-white) !important;
          padding: 10px 20px;
          border-radius: 6px;
          font-weight: 600; font-size: 14px;
        }
        .vs-nav-cta:hover { background: var(--vs-dark-navy); }
        @media (max-width: 720px) {
          .vs-nav-links a:not(.vs-nav-cta):not(.vs-nav-login) { display: none; }
          .vs-nav-links { gap: 12px; }
        }

        /* Hero */
        .vs-hero {
          padding: 96px 24px 80px;
          text-align: center;
          background: linear-gradient(180deg, var(--vs-off-white) 0%, var(--vs-light-blue-gray) 100%);
        }
        .vs-hero-logo {
          max-width: 480px; width: 80%;
          height: auto; margin: 0 auto 32px;
          display: block;
        }
        .vs-hero-h1 {
          font-size: clamp(28px, 4.5vw, 44px);
          font-weight: 700;
          color: var(--vs-dark-navy);
          letter-spacing: -0.02em; line-height: 1.15;
          max-width: 820px; margin: 0 auto 20px;
        }
        .vs-hero-h1 .vs-accent { color: var(--vs-cyan-blue); }
        .vs-hero-sub {
          font-size: clamp(16px, 2vw, 19px);
          color: var(--vs-slate);
          max-width: 680px; margin: 0 auto 40px;
          line-height: 1.6;
        }
        .vs-hero-actions {
          display: flex; gap: 16px; justify-content: center; flex-wrap: wrap;
        }
        .vs-btn-primary {
          background: var(--vs-cyan-blue); color: var(--vs-white);
          padding: 14px 28px; border-radius: 8px;
          font-weight: 600; font-size: 15px;
          border: none; cursor: pointer;
          transition: background 0.15s ease, transform 0.15s ease;
          display: inline-block; text-decoration: none;
        }
        .vs-btn-primary:hover {
          background: var(--vs-dark-navy); color: var(--vs-white);
          transform: translateY(-1px);
        }
        .vs-btn-secondary {
          background: transparent; color: var(--vs-dark-navy);
          padding: 14px 28px; border-radius: 8px;
          font-weight: 600; font-size: 15px;
          border: 1.5px solid var(--vs-dark-navy);
          transition: all 0.15s ease;
          display: inline-block; text-decoration: none;
        }
        .vs-btn-secondary:hover {
          background: var(--vs-dark-navy); color: var(--vs-white);
        }
        .vs-btn-ghost {
          background: transparent; color: var(--vs-slate);
          padding: 14px 28px; border-radius: 8px;
          font-weight: 600; font-size: 15px;
          border: 1.5px solid var(--vs-light-blue-gray);
          transition: all 0.15s ease;
          display: inline-block; text-decoration: none;
        }
        .vs-btn-ghost:hover {
          border-color: var(--vs-cyan-blue);
          color: var(--vs-cyan-blue);
        }

        /* Sections */
        .vs-section { padding: 80px 24px; }
        .vs-section-light { background: var(--vs-white); }
        .vs-section-muted { background: var(--vs-off-white); }

        .vs-section-eyebrow {
          font-size: 13px; font-weight: 600; letter-spacing: 0.12em;
          text-transform: uppercase; color: var(--vs-cyan-blue);
          text-align: center; margin-bottom: 12px;
        }
        .vs-section-title {
          font-size: clamp(26px, 3.5vw, 36px);
          font-weight: 700; color: var(--vs-dark-navy);
          text-align: center; letter-spacing: -0.02em;
          line-height: 1.2; margin-bottom: 16px;
        }
        .vs-section-sub {
          font-size: 17px; color: var(--vs-slate);
          text-align: center; max-width: 640px;
          margin: 0 auto 56px;
        }

        /* Stack cards */
        .vs-stacks-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 24px; max-width: 1080px; margin: 0 auto;
        }
        .vs-stack-card {
          background: var(--vs-white);
          border: 1px solid var(--vs-light-blue-gray);
          border-radius: 12px; padding: 28px 24px;
          transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
        }
        .vs-stack-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(26, 50, 102, 0.08);
          border-color: var(--vs-cyan-blue);
        }
        .vs-stack-icon {
          width: 44px; height: 44px;
          border-radius: 10px;
          background: linear-gradient(135deg, var(--vs-cyan-blue) 0%, var(--vs-dark-navy) 100%);
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 16px; color: var(--vs-white);
          font-weight: 700; font-size: 18px;
        }
        .vs-stack-name {
          font-size: 18px; font-weight: 700;
          color: var(--vs-dark-navy); margin-bottom: 8px;
        }
        .vs-stack-desc {
          font-size: 14px; color: var(--vs-slate); line-height: 1.6;
        }

        /* Personas */
        .vs-personas {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 20px; max-width: 1080px; margin: 0 auto;
        }
        .vs-persona {
          background: var(--vs-white);
          border-radius: 12px; padding: 32px 24px;
          text-align: center;
          border: 1px solid var(--vs-light-blue-gray);
        }
        .vs-persona-emoji { font-size: 32px; margin-bottom: 12px; line-height: 1; }
        .vs-persona-name {
          font-size: 17px; font-weight: 700;
          color: var(--vs-dark-navy); margin-bottom: 8px;
        }
        .vs-persona-desc {
          font-size: 14px; color: var(--vs-slate); line-height: 1.55;
        }

        /* Contact CTA */
        .vs-contact-cta {
          background: var(--vs-deep-dark); color: var(--vs-white);
          padding: 80px 24px; text-align: center;
        }
        .vs-contact-cta .vs-section-eyebrow { color: var(--vs-vibrant-cyan); }
        .vs-contact-cta h2 {
          font-size: clamp(26px, 3.5vw, 36px);
          font-weight: 700; color: var(--vs-white);
          margin-bottom: 16px; letter-spacing: -0.02em;
        }
        .vs-contact-cta p {
          font-size: 17px; color: var(--vs-light-blue-gray);
          max-width: 560px; margin: 0 auto 32px;
        }
        .vs-contact-cta .vs-btn-primary {
          background: var(--vs-vibrant-cyan); color: var(--vs-deep-dark);
        }
        .vs-contact-cta .vs-btn-primary:hover {
          background: var(--vs-white);
        }

        /* Footer */
        .vs-footer {
          background: var(--vs-dark-navy);
          color: var(--vs-light-blue-gray);
          padding: 48px 24px 32px;
        }
        .vs-footer-inner { max-width: 1140px; margin: 0 auto; }
        .vs-footer-top {
          display: grid;
          grid-template-columns: 1.5fr 1fr 1fr;
          gap: 32px; margin-bottom: 32px;
          padding-bottom: 32px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        @media (max-width: 640px) { .vs-footer-top { grid-template-columns: 1fr; } }
        .vs-footer-tagline {
          font-size: 16px; color: var(--vs-vibrant-cyan);
          font-weight: 500; margin-bottom: 12px;
        }
        .vs-footer-address {
          font-size: 13px; color: var(--vs-tag-gray); line-height: 1.7;
        }
        .vs-footer-col h4 {
          font-size: 12px; font-weight: 600; letter-spacing: 0.1em;
          text-transform: uppercase; color: var(--vs-white);
          margin-bottom: 12px;
        }
        .vs-footer-col a {
          display: block; font-size: 14px;
          color: var(--vs-light-blue-gray); margin-bottom: 8px;
        }
        .vs-footer-col a:hover { color: var(--vs-vibrant-cyan); }
        .vs-footer-bottom {
          display: flex; justify-content: space-between; align-items: center;
          flex-wrap: wrap; gap: 12px;
          font-size: 13px; color: var(--vs-tag-gray);
        }
      `}</style>

      <div className="vs-root">
        <nav className="vs-nav">
          <div className="vs-nav-inner">
            <a href="/" className="vs-nav-logo" aria-label="verticalstack home">
              <img src="/assets/verticalstack_logo_b_text_v2_text_accent.png" alt="verticalstack" />
            </a>
            <div className="vs-nav-links">
              <a href="#what">What We Do</a>
              <a href="#who">Who It's For</a>
              <a href="/pricing">Pricing</a>
              <a href="/demo">View Demo</a>
              <a href="/app" className="vs-nav-login">Log In</a>
              <a href="mailto:hello@verticalstack.ai" className="vs-nav-cta">Contact</a>
            </div>
          </div>
        </nav>

        <header className="vs-hero">
          <img className="vs-hero-logo"
               src="/assets/verticalstack_logo_b_text_v2_text_accent.png"
               alt="verticalstack — stack more, manage less" />
          <h1 className="vs-hero-h1">
            The property ecosystem network. <span className="vs-accent">Owners have choices.</span>
          </h1>
          <p className="vs-hero-sub">
            verticalstack is parcel-centric infrastructure for owners, property managers, subcontractors, and short-term-rental hosts. Solo, hybrid, or fully managed — your stack, your call.
          </p>
          <div className="vs-hero-actions">
            <a href="/pricing" className="vs-btn-primary">See Pricing</a>
            <a href="/demo" className="vs-btn-secondary">View Demo</a>
            <a href="mailto:hello@verticalstack.ai" className="vs-btn-ghost">Get in Touch</a>
          </div>
        </header>

        <section className="vs-section vs-section-light" id="what">
          <div className="vs-container">
            <div className="vs-section-eyebrow">What We Do</div>
            <h2 className="vs-section-title">Every stage of property, in one stack.</h2>
            <p className="vs-section-sub">
              Acquire, develop, operate, maintain, reposition, realize. One parcel-centric platform spans the full lifecycle — from prospecting through exit.
            </p>
            <div className="vs-stacks-grid">
              {[
                ['A','Acquire','Source deals, run comps, close transactions. Scoutstack and Closestack power the buy-side workflow before a parcel is yours.'],
                ['D','Develop','Buildstack manages new construction and rehab projects from RFQ through certificate of occupancy with a vetted subcontractor network.'],
                ['O','Operate','Launchstack and Runstack handle marketing, leasing, tenants, guests, and day-to-day operations across long-term rental, short-term rental, and commercial.'],
                ['M','Maintain','Maintainstack coordinates work orders, vendors, materials, and asset lifecycle. Built-in subcontractor reputation across the network.'],
              ].map(([letter, name, desc]) => (
                <div key={letter} className="vs-stack-card">
                  <div className="vs-stack-icon">{letter}</div>
                  <div className="vs-stack-name">{name}</div>
                  <div className="vs-stack-desc">{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="vs-section vs-section-muted" id="who">
          <div className="vs-container">
            <div className="vs-section-eyebrow">Who It's For</div>
            <h2 className="vs-section-title">Built for everyone in the property stack.</h2>
            <p className="vs-section-sub">
              Neutral infrastructure. Whether you self-manage one cabin or oversee hundreds of doors, verticalstack scales with you.
            </p>
            <div className="vs-personas">
              {[
                ['🏘️','Property Owners','From single-family rentals to mixed portfolios. Solo, hybrid, or fully managed — your choice, your stack.'],
                ['🏢','Property Managers','Run your full operation: tenants, owners, subs, books, K-1s. Multi-workspace architecture for multi-entity portfolios.'],
                ['🔧','Subcontractors','Receive jobs, track materials, get paid through soft-gated payment release. Build a portable reputation across the network.'],
                ['🏖️','STR Hosts','Short-term rental operators get bookings, cleaning coordination, and turnover automation alongside long-term tools.'],
              ].map(([emoji, name, desc]) => (
                <div key={name} className="vs-persona">
                  <div className="vs-persona-emoji">{emoji}</div>
                  <div className="vs-persona-name">{name}</div>
                  <div className="vs-persona-desc">{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="vs-contact-cta" id="contact">
          <div className="vs-section-eyebrow">Get Started</div>
          <h2>Ready to stack?</h2>
          <p>Tell us about your portfolio. We'll show you how the stacks fit together for your situation.</p>
          <div style={{display:'flex', gap:'16px', justifyContent:'center', flexWrap:'wrap'}}>
            <a href="mailto:hello@verticalstack.ai?subject=verticalstack%20%E2%80%94%20Get%20Started"
               className="vs-btn-primary">Contact Us</a>
            <a href="/demo"
               style={{background:'transparent', color:'var(--vs-vibrant-cyan)', padding:'14px 28px', borderRadius:'8px', fontWeight:'600', fontSize:'15px', border:'1.5px solid var(--vs-vibrant-cyan)', display:'inline-block', textDecoration:'none', transition:'all 0.15s ease'}}
               onMouseOver={e=>{e.target.style.background='var(--vs-vibrant-cyan)';e.target.style.color='var(--vs-deep-dark)'}}
               onMouseOut={e=>{e.target.style.background='transparent';e.target.style.color='var(--vs-vibrant-cyan)'}}>
              View Demo
            </a>
          </div>
        </section>

        <footer className="vs-footer">
          <div className="vs-footer-inner">
            <div className="vs-footer-top">
              <div>
                <div className="vs-footer-tagline">stack more, manage less</div>
                <div className="vs-footer-address">
                  verticalstack, Inc.<br/>
                  315 Tuscarawas St W<br/>
                  Canton, OH 44702<br/>
                  <a href="mailto:hello@verticalstack.ai" style={{color:'var(--vs-light-blue-gray)'}}>hello@verticalstack.ai</a>
                </div>
              </div>
              <div className="vs-footer-col">
                <h4>Product</h4>
                <a href="#what">What We Do</a>
                <a href="#who">Who It's For</a>
                <a href="/pricing">Pricing</a>
                <a href="/demo">View Demo</a>
                <a href="/app">Log In</a>
              </div>
              <div className="vs-footer-col">
                <h4>Company</h4>
                <a href="mailto:hello@verticalstack.ai">Contact</a>
                <a href="/privacy">Privacy Policy</a>
                <a href="/terms">Terms of Service</a>
              </div>
            </div>
            <div className="vs-footer-bottom">
              <div>© 2026 verticalstack, Inc. All rights reserved.</div>
              <div>verticalstack.ai</div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
